import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { subDays, format, startOfDay, endOfDay } from 'date-fns';

export interface AnalyticsDataPoint {
  date: string;
  totalSignals: number;
  successfulForwards: number;
  failedForwards: number;
  successRate: number;
  avgResponseTime: number;
}

export interface EndpointHealth {
  id: string;
  name: string;
  parentBotName: string;
  totalForwards: number;
  successfulForwards: number;
  failedForwards: number;
  successRate: number;
  avgResponseTime: number;
  lastActive: string;
  status: 'healthy' | 'warning' | 'critical';
}

export interface ResponseTimeDistribution {
  range: string;
  count: number;
  percentage: number;
}

export const useBotMergeAnalytics = (days: number = 7) => {
  const endDate = new Date();
  const startDate = subDays(endDate, days);

  // Daily trend data
  const { data: trendData = [], isLoading: trendLoading } = useQuery({
    queryKey: ['bot-merge-analytics-trend', days],
    queryFn: async (): Promise<AnalyticsDataPoint[]> => {
      const { data: events, error: eventsError } = await supabase
        .from('bot_signal_events')
        .select('created_at')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (eventsError) throw eventsError;

      const { data: logs, error: logsError } = await supabase
        .from('bot_signal_forward_logs')
        .select('created_at, success, duration_ms')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (logsError) throw logsError;

      // Group data by day
      const dailyData: { [key: string]: AnalyticsDataPoint } = {};

      // Initialize all days
      for (let i = 0; i < days; i++) {
        const date = format(subDays(endDate, i), 'yyyy-MM-dd');
        dailyData[date] = {
          date,
          totalSignals: 0,
          successfulForwards: 0,
          failedForwards: 0,
          successRate: 0,
          avgResponseTime: 0,
        };
      }

      // Count signals per day
      events?.forEach(event => {
        const date = format(new Date(event.created_at), 'yyyy-MM-dd');
        if (dailyData[date]) {
          dailyData[date].totalSignals++;
        }
      });

      // Count forwards per day
      const dailyResponseTimes: { [key: string]: number[] } = {};
      logs?.forEach(log => {
        const date = format(new Date(log.created_at), 'yyyy-MM-dd');
        if (dailyData[date]) {
          if (log.success === true) {
            dailyData[date].successfulForwards++;
            if (log.duration_ms) {
              if (!dailyResponseTimes[date]) dailyResponseTimes[date] = [];
              dailyResponseTimes[date].push(log.duration_ms);
            }
          } else {
            dailyData[date].failedForwards++;
          }
        }
      });

      // Calculate success rates and avg response times
      Object.keys(dailyData).forEach(date => {
        const data = dailyData[date];
        const totalForwards = data.successfulForwards + data.failedForwards;
        data.successRate = totalForwards > 0 ? (data.successfulForwards / totalForwards) * 100 : 0;
        
        const responseTimes = dailyResponseTimes[date] || [];
        data.avgResponseTime = responseTimes.length > 0 
          ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length 
          : 0;
      });

      return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Endpoint health data
  const { data: endpointHealth = [], isLoading: healthLoading } = useQuery({
    queryKey: ['bot-merge-endpoint-health', days],
    queryFn: async (): Promise<EndpointHealth[]> => {
      const { data, error } = await supabase
        .from('bot_signal_forward_endpoints')
        .select(`
          id,
          name,
          bot:bot_id (
            name
          )
        `)
        .eq('is_active', true);

      if (error) throw error;

      const endpoints = data || [];
      const healthData: EndpointHealth[] = [];

      for (const endpoint of endpoints) {
        const { data: logs, error: logsError } = await supabase
          .from('bot_signal_forward_logs')
          .select('success, duration_ms, created_at')
          .eq('endpoint_id', endpoint.id)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());

        if (logsError) continue;

        const totalForwards = logs?.length || 0;
        const successfulForwards = logs?.filter(log => log.success === true).length || 0;
        const failedForwards = totalForwards - successfulForwards;
        const successRate = totalForwards > 0 ? (successfulForwards / totalForwards) * 100 : 0;

        const responseTimes = logs?.filter(log => 
          log.success === true && log.duration_ms !== null
        ).map(log => log.duration_ms!) || [];
        
        const avgResponseTime = responseTimes.length > 0 
          ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length 
          : 0;

        const lastActive = logs && logs.length > 0 
          ? logs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].created_at
          : new Date(0).toISOString();

        // Determine status
        let status: 'healthy' | 'warning' | 'critical' = 'healthy';
        if (successRate < 50 || avgResponseTime > 5000) {
          status = 'critical';
        } else if (successRate < 80 || avgResponseTime > 2000) {
          status = 'warning';
        }

        healthData.push({
          id: endpoint.id,
          name: endpoint.name,
          parentBotName: (endpoint.bot as any)?.[0]?.name || 'Unknown',
          totalForwards,
          successfulForwards,
          failedForwards,
          successRate: Math.round(successRate * 100) / 100,
          avgResponseTime: Math.round(avgResponseTime),
          lastActive,
          status,
        });
      }

      return healthData.sort((a, b) => b.totalForwards - a.totalForwards);
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Response time distribution
  const { data: responseTimeDistribution = [], isLoading: distributionLoading } = useQuery({
    queryKey: ['bot-merge-response-distribution', days],
    queryFn: async (): Promise<ResponseTimeDistribution[]> => {
      const { data, error } = await supabase
        .from('bot_signal_forward_logs')
        .select('duration_ms')
        .eq('success', true)
        .not('duration_ms', 'is', null)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) throw error;

      const responseTimes = data?.map(log => log.duration_ms!) || [];
      const total = responseTimes.length;

      if (total === 0) return [];

      const ranges = [
        { min: 0, max: 500, label: '0-500ms' },
        { min: 500, max: 1000, label: '500ms-1s' },
        { min: 1000, max: 2000, label: '1-2s' },
        { min: 2000, max: 5000, label: '2-5s' },
        { min: 5000, max: Infinity, label: '5s+' },
      ];

      return ranges.map(range => {
        const count = responseTimes.filter(time => 
          time >= range.min && time < range.max
        ).length;

        return {
          range: range.label,
          count,
          percentage: Math.round((count / total) * 100 * 100) / 100,
        };
      }).filter(item => item.count > 0);
    },
    refetchInterval: 60000,
  });

  return {
    trendData,
    endpointHealth,
    responseTimeDistribution,
    isLoading: trendLoading || healthLoading || distributionLoading,
  };
};